/**
 * Both UIs fetch runtime config when they first render. The shell has already
 * fetched it to make the UI decision, so it answers that first request with the
 * same response body instead of a second network round trip.
 *
 * Only one plain GET of the exact URL is answered, and only for a short while.
 * A request that asks for a fresh copy (`cache: 'no-store'`, as the build
 * refresh kill-switch check does) always goes to the network.
 */
export const RUNTIME_CONFIG_HANDOFF_TTL_MS = 30_000;

export function handOffRuntimeConfig({
  url,
  body,
  target = window,
  now = () => Date.now(),
  ttlMs = RUNTIME_CONFIG_HANDOFF_TTL_MS,
}: {
  url: string;
  body: string;
  target?: Pick<Window, 'fetch'>;
  now?: () => number;
  ttlMs?: number;
}): void {
  const network = target.fetch;
  const expiresAt = now() + ttlMs;
  let pending: string | null = body;
  target.fetch = function runtimeConfigHandoffFetch(input, init) {
    if (
      pending !== null &&
      typeof input === 'string' &&
      input === url &&
      (init?.method ?? 'GET').toUpperCase() === 'GET' &&
      init?.body == null &&
      init?.cache !== 'no-store' &&
      !init?.signal?.aborted
    ) {
      const handed = pending;
      pending = null;
      if (now() <= expiresAt) {
        return Promise.resolve(
          new Response(handed, {
            status: 200,
            headers: { 'content-type': 'application/json' },
          })
        );
      }
    }
    return network.call(target, input, init);
  } as typeof fetch;
}
